import {
  Loading,
} from '@app/components';
import { useRatingsFeedback } from './RatingsFeedback.hooks';
import { FeedbackReviewBox } from './FeedbackReviewBox';
import { BusinessReviews } from '@app/models/reviews';
import NoFeedbackIllustration from '@app/assets/illustrations/no-feedback.svg?react';
import { Button } from '@app/components/Button/Button';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import {PreviewReviewOld} from "@app/components/PreviewReview/PreviewReviewOld.tsx";

export const RatingsFeedback = () => {
  const navigate = useNavigate();
  const {
    filtersForm,
    requestData,
    handleReviewClick,
    activeReview,
    isLoading,
    tabOptions,
    handleTabClick,
    activeTab,
    handleResolveReview,
    isResolving,
  } = useRatingsFeedback();
  

  const [searchValue, setSearchValue] = useState(filtersForm.getValues('search') || '');
  const [filteredReviews, setFilteredReviews] = useState<BusinessReviews[] | null>(null);

  // Debounce search input
  React.useEffect(() => {
    const handler = setTimeout(() => {
      filtersForm.setValue('search', searchValue);
      // Auto-search when typing
      if (searchValue.trim() === '') {
        setFilteredReviews(null);
        // When clearing search, check if current active review still exists in original list
        if (activeReview && requestData?.data?.reviews) {
          const reviewExists = requestData.data.reviews.find(review => review.id === activeReview);
          if (!reviewExists) {
            handleReviewClick('');
          }
        }
      } else {
        performSearch(searchValue);
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [searchValue, activeReview, requestData?.data?.reviews]);

  // Handler for search
  const performSearch = (searchTerm: string) => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) {
      setFilteredReviews(null);
      return;
    }
    const allReviews = requestData?.data?.reviews || [];
    const filtered = allReviews.filter(
      (review) =>
        (review.review && review.review.toLowerCase().includes(search)) ||
        (review.reply && review.reply.toLowerCase().includes(search)) ||
        (review.customer?.firstName?.toLowerCase().includes(search)) ||
        (review.customer?.lastName?.toLowerCase().includes(search)) ||
        (review.customer?.firstName && review.customer?.lastName && 
         `${review.customer.firstName} ${review.customer.lastName}`.toLowerCase().includes(search))
    );
    setFilteredReviews(filtered);
    
    // Only clear active review if the currently selected feedback is not in search results
    if (activeReview && !filtered.find(review => review.id === activeReview)) {
      handleReviewClick('');
    }
  };

  // Handler for search icon click
  const handleSearchClick = () => {
    performSearch(searchValue);
  };

  return (
    <div>
      <div className="bg-white border-b border-b-base-ghostWhite">
        <div className="container mx-auto p-4 flex flex-col md:flex-row justify-between items-center">
          <h2 className="font-bold text-xl md:text-[28px]">Feedback</h2>
        </div>


        {/* Tabs for filtering feedback */}
        {requestData?.data?.count > 0 && (
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-3 sm:gap-6 border-b border-base-ghostGray overflow-x-auto">
              {tabOptions.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => handleTabClick(tab.value)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.value
                      ? 'border-black text-black'
                      : 'border-transparent text-base-coolGray hover:text-black'
                  }`}
                >
                  <span className="font-medium text-sm sm:text-base">{tab.label}</span>
                  <span className="text-xs sm:text-sm bg-gray-100 px-2 py-0.5 rounded-md">
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search row */}
        {requestData?.data?.count > 0 && requestData?.data?.reviews && requestData?.data?.reviews.length > 0 && (
          <div className="container mx-auto flex justify-between items-center py-4 sm:py-6 px-4">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold">
              {filteredReviews ? filteredReviews.length : requestData?.data?.reviews?.length || 0}{' '}
              feedback
            </h3>
            <div className="relative w-full max-w-sm sm:max-w-xs md:max-w-md">
                <input
                  type="text"
                  className="w-full border-0 border-b border-base-ghostGray text-base sm:text-lg py-2 pr-10 focus:outline-none focus:ring-0 focus:border-primary-100 bg-transparent placeholder:text-base-coolGray"
                  placeholder="Search"
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearchClick()}
                  style={{ fontWeight: 400 }}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-base-coolGray"
                  onClick={handleSearchClick}
                  aria-label="Search"
                >
                  <svg width="24" height="24" className="sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/><path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M21 21l-3.5-3.5"/></svg>
                </button>
            </div>
          </div>
        )}


      </div>

      {/* Main loading state */}
      {isLoading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loading />
        </div>
      )}

      {requestData?.data?.count > 0 && !isLoading && requestData?.data?.reviews && requestData?.data?.reviews.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-0 lg:gap-4 min-h-screen">
          <div className="col-span-1 lg:col-span-2 bg-white h-full">
            {filteredReviews && filteredReviews.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center justify-center flex-col max-w-[280px] mx-auto space-y-4 px-4">
                  <div className="w-16 h-16 bg-[#F6F7FA] rounded-lg flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.9265 15.0416L18.3996 18.4016M17.2796 9.44156C17.2796 13.7715 13.7695 17.2816 9.43961 17.2816C5.1097 17.2816 1.59961 13.7715 1.59961 9.44156C1.59961 5.11165 5.1097 1.60156 9.43961 1.60156C13.7695 1.60156 17.2796 5.11165 17.2796 9.44156Z" stroke="#8B919F" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p className="font-bold text-base text-center">
                    No feedback found
                  </p>
                  <p className="text-sm text-base-coolGray text-center leading-relaxed">
                    No feedback was found based on your search.
                  </p>
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {(filteredReviews || requestData?.data?.reviews || []).map((review: BusinessReviews) => (
                  <li
                    key={review.id}
                    onClick={() => handleReviewClick(review.id)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <PreviewReviewOld
                      review={review}
                      active={activeReview === review.id}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="col-span-1 lg:col-span-4 p-4">
            {activeReview ? (
              (() => {
                const selectedReview = requestData?.data?.reviews?.find(
                  (review: BusinessReviews) => review.id === activeReview
                );
                return selectedReview ? (
                  <FeedbackReviewBox
                    review={selectedReview}
                    onResolve={handleResolveReview}
                    isResolving={isResolving}
                  />
                ) : (
                  <div className="hidden lg:flex items-center justify-center h-64">
                    <p className="text-base-coolGray text-center">
                      Selected feedback is no longer available
                    </p>
                  </div>
                );
              })()
            ) : (
              <div className="hidden lg:flex items-center justify-center h-64">
              </div>
            )}
          </div>
        </div>
      )}
      
      {requestData?.data?.count === 0 && !isLoading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center justify-center flex-col max-w-[280px] sm:max-w-[350px] mx-auto space-y-3 sm:space-y-4 px-4 mt-32">
            <NoFeedbackIllustration className="w-16 h-16 sm:w-auto sm:h-auto" />
            <p className="font-bold text-base sm:text-lg text-center">No feedback</p>
            <p className="text-sm sm:text-base text-base-coolGray text-center leading-relaxed">
              If a customer provides non-public feedback, it will be shown here.
              <br /><br />
              Start a campaign to send review requests across your platforms.
            </p>
            <Button
              type="button"
              onClick={() => navigate('/campaigns?schedule=true')}
            >
              Schedule a campaign
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
